import { Injectable } from '@angular/core';
import { LOCAL_STORAGE_KEYS, ROLES_PERMISSIONS } from '../constants/roles-permissions.constants';
import { BehaviorSubject, catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { UserRoleService } from '../modules/settings/location/services/user-role.service';
import { SharedDataService } from '../shared/service/shared-data/shared-data.service';
import { PERMISSION_BREADCRUMBS } from '../constants/permission-breadcrumbs';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private userRole: string | null = null;
  private userPermissionsSubject = new BehaviorSubject<string[]>([]);
  userPermissions$ = this.userPermissionsSubject.asObservable(); // Permissions received after login  
  private currentPermissionsSubject = new BehaviorSubject<string[]>([]);//permissions of current selected lcoation
  public currentPermissions$ = this.currentPermissionsSubject.asObservable();
  userPermissions: string[] = [];
  private currentLocPermissionsCache = new Map<string, Observable<string[]>>();
  private isAdminUser: boolean = false;
  private basePermissions$?: Observable<string[]>;//stores the shared observable after the first request
  constructor(private userRoleService:UserRoleService,
    private sharedDataService: SharedDataService
  ) {
    if(sessionStorage.getItem('selLocationId')&& sessionStorage.getItem('ACCESS_TOKEN')) {
      this.getCurrentLocPermissions(sessionStorage.getItem('selLocationId')||'')
    }
     if(sessionStorage.getItem('ACCESS_TOKEN')&&sessionStorage.getItem('IsAdminUser')) {
       const storedAdmin = sessionStorage.getItem('IsAdminUser');
       this.isAdminUser = storedAdmin === 'true';
     }
  }

  /**
   * Load the user's role and permissions from local storage.
   */
  loadUserRoleAndPermissions(userRole: string = '', currentLocation: string = ''): Observable<string[]> {
  if (userRole) {
    sessionStorage.setItem('UserRole', userRole);
    this.userRole = userRole;
  }
  const userLocationId = sessionStorage.getItem('baseLocationId') || currentLocation;

  if (!userLocationId) {
    this.userPermissions = [];
    this.userPermissionsSubject.next([]);
    this.isAdminUser = false;
    return of([]);
  }

  if (this.basePermissions$) {
    return this.basePermissions$;
  }

    this.basePermissions$ = this.userRoleService.getUserPermissionsByLocation(userLocationId).pipe(
      map((response: any) => {
        const apiData = response?.body;
        this.userPermissions = apiData?.features || [];
        this.userPermissionsSubject.next(this.userPermissions); // Emit the updated permissions
        this.isAdminUser = response?.body?.isAdmin ?? false;
        sessionStorage.setItem('IsAdminUser', String(this.isAdminUser)); // Save to localStorage
        return this.userPermissions;
      }),
      catchError(() => {
        this.userPermissions = [];
        this.userPermissionsSubject.next([]);this.isAdminUser = false;
        sessionStorage.setItem('IsAdminUser', 'false');
        return of([]);
      }),
      shareReplay(1)
    );

    return this.basePermissions$;
} 
  /**
   * Get stored roles from local storage.
   */
  getStoredRoles(): any[] {
    const data = sessionStorage.getItem(LOCAL_STORAGE_KEYS.ROLES_PERMISSIONS);
    return data ? JSON.parse(data) : [];
  }
  /**
   * Check if user has a specific permission
   * @param permission - The permission to check
   * @returns boolean
   */
  hasPermission(permission: string): boolean {// if the base location has that permission
    return this.userPermissionsSubject.getValue().includes(permission);
  }

  /**
   * Check if user has edit (WRITE) permissions
   * @param entity - The entity name (e.g., 'PLANNING_SHEET', 'STAFF')
   * @returns boolean
   */

  /**
   * Check if user has restricted access (i.e., neither read nor write)
   * @param entity - The entity name (e.g., 'PLANNING_SHEET', 'STAFF')
   * @returns boolean
   */
  isRestricted(entity: string): boolean {
    return !this.hasPermission(entity);
  }
  /**
   * Update the user's role and reload permissions.
   * @param role - The new role to set
   */
  setUserRole(role: string): void {
    sessionStorage.setItem('UserRole', role);
    this.loadUserRoleAndPermissions(); // Reload permissions
  }
  /**
   * Get the user's current role.
   * @returns string | null
   */
  getUserRole(): string | null {
    if (!this.userRole) {
      this.userRole = sessionStorage.getItem('UserRole');
    }
    return this.userRole;
  }
  getPermissionsForLocation(locationId: string): string[] {//to return the features of a particular location
    const roles = this.getStoredRoles(); // Retrieve stored roles and permissions
    const roleData = roles.find((r) => r.roleName === this.userRole);
  
    if (roleData && roleData.locations?.length) {
      const matchedLocation = roleData.locations.find((loc: any) => loc.locationId === locationId);
      return matchedLocation ? matchedLocation.features : []; // Return features (permissions) of the location
    }
    
    return []; // Return empty array if no permissions are found
  }
  checkIsAdmin(): boolean{
    return this.isAdminUser;
  }
  getCurrentLocPermissions(locationId: string = ''): Observable<string[]> {
  if (!locationId) {
    locationId = sessionStorage.getItem('selLocationId') || '';
  }

  // If still no locationId, safely return empty permissions
  if (!locationId) {
    this.currentPermissionsSubject.next([]);
    return of([]);
  }

  let permissions$: Observable<string[]>;

  if (this.currentLocPermissionsCache.has(locationId)) {
    permissions$ = this.currentLocPermissionsCache.get(locationId)!.pipe(
      tap((features) => this.currentPermissionsSubject.next(features))
    );
  } else {
    permissions$ = this.userRoleService.getUserPermissionsByLocation(locationId).pipe(
      map((response) => response?.body?.features || []),
      tap((features) => this.currentPermissionsSubject.next(features)),
      catchError(() => {
        this.currentPermissionsSubject.next([]);
        return of([]);
      }),
      shareReplay(1)
    );
    this.currentLocPermissionsCache.set(locationId, permissions$);
  }

  return permissions$;
}



  getBasePermissions(): string[] { // return the permissions of the user in current location
  return this.userPermissionsSubject.getValue();
  }
   hasCurrentLocPermission(permission: string): boolean {// if the current selected location has that permission
    return this.currentPermissionsSubject.getValue().includes(permission);
  }
  resetBasePermissionsCache(): void {
    this.basePermissions$ = undefined;
  }
  getPermissionPath(permissionKey: string): string {
  return PERMISSION_BREADCRUMBS[permissionKey] || 'Other';
  }
   clearState(): void {
    this.userRole = null;
    this.isAdminUser = false;

    this.userPermissionsSubject.next([]);
    this.currentPermissionsSubject.next([]);

    this.currentLocPermissionsCache.clear();
    this.basePermissions$ = undefined;
  }
}
